import { BaseModel } from "../../../../../core/util/BaseModel";
import { action, computed, makeObservable, observable } from "mobx";
import ActionVisualisationsHub, {
  ActionVisualisationsHub as IActionVisualisationsHub
} from "../../../../../services/hubs/ActionVisualisationsHub/ActionVisualisations_hub";
import I18n from "../../../../../core/localization/I18n";
import { minimumDate, ProgressStatus, RagStatus } from "../../../../../enums";
import ProjectsApi, { ProjectsApi as IProjectsApi } from "../../../../../services/api/v2/projects/Projects.api";
import LocalStorageService from "../../../../../services/local/localStorageService/LocalStorageService";
import * as _ from "lodash";
import { ReportFilterAddonsKeys } from "../../../../../core/reportFilter/ReportFilterAddon";
import ActionsApi, { ActionsApi as IActionsApi } from "../../../../../services/api/v2/actions/Actions.api";
import moment from "moment";

export class ActionReportingViewModel extends BaseModel {
  navigate: any;
  @observable.ref project: FP.Entities.IProject;
  @observable actions: FP.Entities.IActionSummary[] = [];
  @observable actionCount: number;
  @observable filteredActions: FP.Entities.IActionSummary[] = [];
  organisationId: number;
  projectId: number;
  @observable currentPage?: string;
  @observable.ref actionRagStatus: any;
  @observable isLoading: boolean = true;
  actionVisualisationsHub: IActionVisualisationsHub;
  projectProvider: IProjectsApi;
  @observable ragData: any[];
  @observable totalConfidenceData: any[];
  @observable totalConfidenceMetric: string;
  @observable filterJSON: any = {};
  localStorage = LocalStorageService;
  actionsProvider: IActionsApi;
  @observable projectActionTags: FP.Entities.ITag[];

  @computed get hasFilters(): boolean {
    var res = false;
    _.forEach(this.filterJSON, (e: any) => {
      res = res || e.length > 0;
    });

    return res;
  }

  constructor(organisationId: number, projectId: number, navigate, page: string) {
    super();
    makeObservable(this);
    this.organisationId = organisationId;
    this.projectId = projectId;
    this.navigate = navigate;
    this.actionsProvider = ActionsApi;
    this.currentPage = page || "";
    this.actionVisualisationsHub = ActionVisualisationsHub;
    this.projectProvider = ProjectsApi;
    let filters = this.localStorage.get(this.getStorageKey(organisationId, projectId));
    if (filters) {
      this.setFilterJSON(JSON.parse(filters));
    }
  }

  onMount = async () => {
    await this.registerSocketEvents();
    await this.loadTags();
    await this.loadProjectInfo(this.projectId);
    this.onFilterChange(_.map(this.filterJSON, (v, k) => ({ key: k, ids: v })));
  };

  onUnmount = () => {
    this.actionVisualisationsHub.stopConnection();
  };

  loadTags = async () => {
    const res = await this.actionsProvider.getTagsByProjectId(this.organisationId, this.projectId);

    if (!res || res.isError) {
      return false;
    }
    this.setProjectActionTags(res.payload);
  };

  registerSocketEvents = async () => {
    if (this.actionVisualisationsHub.isConnectionStarted === true) {
      await this.actionVisualisationsHub.stopConnection();
    }
    await this.actionVisualisationsHub.startConnection();

    this.actionVisualisationsHub.onLoadGridData(d => {
      this.setData(d);
      this.loadCharts();
      this.setIsLoading(false);
    });

    await this.actionVisualisationsHub.invokeLoadGridData(this.organisationId, this.projectId);
  };

  @action
  setProjectActionTags = (projectActionTags: FP.Entities.ITag[]) => {
    this.projectActionTags = projectActionTags;
  };
  @action
  setIsLoading = (isLoading: boolean) => {
    this.isLoading = isLoading;
  };
  @action
  loadCharts = () => {
    this.loadActionRagStatus();
    this.loadTotalConfidence();
  };

  @action
  loadProjectInfo = async (id: number) => {
    const res = await this.projectProvider.getById(this.organisationId, id);
    this.project = res.payload;
  };

  @action
  setData = actions => {
    this.actions = actions;
    this.filteredActions = actions;
    this.actionCount = actions.length;
  };

  @action
  setFilteredData = actions => {
    this.filteredActions = actions;
    this.actionCount = actions.length;
  };

  @action
  onFilterChange = async k => {
    this.setFilterJSONFromList(k);

    const filteredData = this.actions.filter(e => {
      const actionFilterArray = this.filterJSON[ReportFilterAddonsKeys.ActionTimelineActions] || [];
      const tagFilterArray = this.filterJSON[ReportFilterAddonsKeys.Tags] || [];

      const actionsFilterPass = actionFilterArray.length === 0 || actionFilterArray.includes(e.id);

      const filteredProjectActionTags = this.projectActionTags
        .filter(pat => tagFilterArray.includes(pat.id))
        .map(pat => pat.text);

      const tagsFilterPass =
        tagFilterArray.length === 0 || e.tags?.split("|").some(tag => filteredProjectActionTags.includes(tag));

      return !!actionsFilterPass && !!tagsFilterPass;
    });

    this.setFilteredData(filteredData);

    this.loadCharts();

    return null;
  };

  @action
  loadActionRagStatus = () => {
    this.ragData = [
      {
        label: I18n.t("visualisations.Red"),
        value: this.filteredActions.filter(
          e => e.ragStatus === RagStatus.RED && e.progressStatus !== ProgressStatus.COMPLETED
        ).length,
        id: "red"
      },
      {
        label: I18n.t("visualisations.Orange"),
        value: this.filteredActions.filter(
          e => e.ragStatus === RagStatus.AMBER && e.progressStatus !== ProgressStatus.COMPLETED
        ).length,
        id: "orange"
      },
      {
        label: I18n.t("visualisations.Green"),
        value: this.filteredActions.filter(
          e => e.ragStatus === RagStatus.GREEN && e.progressStatus !== ProgressStatus.COMPLETED
        ).length,
        id: "green"
      },
      {
        label: I18n.t("visualisations.Completed"),
        value: this.filteredActions.filter(e => e.progressStatus === ProgressStatus.COMPLETED).length,
        id: "completed"
      },
      {
        label: I18n.t("visualisations.Unknown"),
        value: this.filteredActions.filter(e => e.ragStatus === -1 && e.progressStatus !== ProgressStatus.COMPLETED)
          .length,
        id: "unknown"
      }
    ];
  };

  @action
  loadTotalConfidence = () => {
    let confidentData = this.actions.filter(
      e =>
        e.startDate !== null &&
        e.endDate !== null &&
        moment(e.endDate) > moment(minimumDate) &&
        moment(e.startDate) > moment(minimumDate)
    ).length;

    let unconfidentData = this.actions.filter(
      e =>
        e.startDate === null ||
        e.endDate === null ||
        moment(e.startDate) <= moment(minimumDate) ||
        moment(e.endDate) <= moment(minimumDate)
    ).length;

    this.totalConfidenceData = [
      {
        label: I18n.t("visualisations.confidentData"),
        value: confidentData,
        id: "confidentData"
      },
      {
        label: I18n.t("visualisations.unconfidentData"),
        value: unconfidentData,
        id: "unconfidentData"
      }
    ];

    this.totalConfidenceMetric = this.getConfidenceMetric(this.totalConfidenceData, confidentData);
  };

  getConfidenceMetric = (allData: any[], confidentData: any): string => {
    let count = allData.reduce((prev, cur) => prev + cur.value, 0);
    if (!count) return "";

    return ((confidentData / count) * 100).toFixed(0);
  };

  changeCurrentView = (newTabIndex: number) => {
    if (newTabIndex === 1) {
      this.navigate(`/organisations/${this.organisationId}/projects/${this.projectId}/actions/visualisations`);
      return;
    }
    this.navigate(`/organisations/${this.organisationId}/projects/${this.projectId}/actions`);
  };

  @action
  setFilterJSON = (filterJSON: any) => {
    this.filterJSON = filterJSON;
    this.localStorage.set(this.getStorageKey(this.organisationId, this.projectId), JSON.stringify(filterJSON));
  };

  @action
  setFilterJSONFromList = (filterJSON: any[]) => {
    let s = {};

    filterJSON.forEach(e => {
      s[e.key] = e.ids;
    });
    this.setFilterJSON(s);
  };

  resetLocalStorage = () => {
    this.localStorage.remove(this.getStorageKey(this.organisationId, this.projectId));
  };

  getStorageKey = (organisationId, projectId) => {
    return `action-reporting-${organisationId}-${projectId}`;
  };
}
